// ===========================================
// Onboarding check-in (enquiries Phase 6, plan §11a.8)
// ===========================================
// Every new student's first weeks: a checklist per student, due a set number of days after their
// first class. Read with `student_onboarding.read`; ticking needs `.write`. The state chip and the
// due day come from the server (the school's day), never the browser's. The linked enquiry chips
// appear only when the server sends them (the viewer may read enquiries).

import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import SettlingInStateChip from '../../components/admin/settlingIn/SettlingInStateChip';
import { useSettlingIns, useTickSettlingInItem } from '../../hooks/useSettlingIn';
import { usePermissions } from '../../hooks/usePermissions';
import { useEnquiryConfig } from '../../hooks/useEnquiries';
import { useSchoolTimezone, useTenant } from '../../contexts/TenantContext';
import { formatInSchoolTz, formatUTCDate } from '../../utils/dateUtils';
import type { SettlingInRow, SettlingInStatus } from '../../api/settlingIn.api';
import { STAGE_COLORS, stageLabel } from '../../components/admin/enquiries/enquiryUi';

const TABS: ReadonlyArray<{ value: SettlingInStatus; label: string; empty: string }> = [
  { value: 'open', label: 'Open', empty: 'Every checklist is finished.' },
  { value: 'due', label: 'Due', empty: 'Nothing is due: every checklist past its window is finished.' },
  { value: 'done', label: 'Done', empty: 'No checklists finished in the last 90 days.' },
  { value: 'all', label: 'All', empty: 'No onboarding check-ins yet.' },
];

const day = (value: string | null): string =>
  value ? formatUTCDate(value, { day: '2-digit', month: '2-digit', year: 'numeric' }, 'en-AU') : '';

/** Visually hidden, read by a screen reader (the read-only checklist's state, /qa F-L5). */
const SR_ONLY = {
  position: 'absolute', width: 1, height: 1, p: 0, m: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
} as const;

function EnquiryChips({ row }: { row: SettlingInRow }) {
  const { schoolSlug } = useTenant();
  const { can } = usePermissions();
  // The config is an enquiries.read route: gated on the permission (#272), and only when there is a chip.
  const { data: config } = useEnquiryConfig(can('enquiries', 'read') && (row.enquiries?.length ?? 0) > 0);
  if (!row.enquiries || row.enquiries.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {row.enquiries.map((e) => (
        <Chip
          key={e.id}
          component={RouterLink}
          to={`/${schoolSlug}/admin/enquiries?enquiry=${encodeURIComponent(e.id)}`}
          clickable
          size="small"
          data-testid="settling-in-enquiry-chip"
          label={`Enquiry · ${stageLabel(config, e.stage)}`}
          sx={STAGE_COLORS[e.stage] ? { bgcolor: STAGE_COLORS[e.stage].bg, color: STAGE_COLORS[e.stage].text } : undefined}
        />
      ))}
    </Stack>
  );
}

function CheckInRow({ row, canTick }: { row: SettlingInRow; canTick: boolean }) {
  const [open, setOpen] = useState(false);
  const tick = useTickSettlingInItem();
  const timeZone = useSchoolTimezone();
  const checklistId = `settling-in-items-${row.studentId}`;

  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }} data-testid="settling-in-row">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
              {row.studentName}
            </Typography>
            <SettlingInStateChip state={row.state} />
            <Typography variant="body2" color="text.secondary">
              {row.done}/{row.total}
            </Typography>
            {row.origin === 'TRIAL' && <Chip size="small" variant="outlined" label="Trial" />}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {row.family ? row.family.name : 'No family'}
            {row.classes.length > 0 ? ` · ${row.classes.join(', ')}` : ''}
          </Typography>
          <Typography variant="body2">
            First class: <strong>{row.firstClassDate ? day(row.firstClassDate) : 'Not scheduled'}</strong>
            {' · '}Due: <strong>{row.dueDate ? day(row.dueDate) : '—'}</strong>
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <EnquiryChips row={row} />
          </Box>
        </Box>
        <IconButton
          size="small"
          aria-label={`${open ? 'Hide' : 'Show'} ${row.studentName}'s checklist`}
          aria-expanded={open}
          // The checklist is unmounted while collapsed: point at it only when it exists.
          aria-controls={open ? checklistId : undefined}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Stack id={checklistId} sx={{ mt: 1 }} spacing={0.5}>
          {row.items.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              This check-in has no checklist items.
            </Typography>
          )}
          {row.items.map((item) => {
            const ticked = item.done && item.completedAt
              ? `Ticked by ${item.completedByName ?? 'someone'}, ${formatInSchoolTz(item.completedAt, timeZone, { day: '2-digit', month: '2-digit', year: 'numeric' })}`
              : null;
            return (
              <Box key={item.id} sx={{ minWidth: 0 }}>
                {canTick ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={item.done}
                        disabled={tick.isPending}
                        onChange={(e) => tick.mutate({ studentId: row.studentId, itemId: item.id, done: e.target.checked })}
                      />
                    }
                    label={item.label}
                    sx={{ mr: 0, '& .MuiFormControlLabel-label': { overflowWrap: 'anywhere' } }}
                  />
                ) : (
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }} data-testid="settling-in-readonly-item">
                    <span aria-hidden="true">{item.done ? '✓' : '○'}</span> {item.label}
                    <Box component="span" sx={SR_ONLY}>{item.done ? ' (done)' : ' (not done)'}</Box>
                  </Typography>
                )}
                {ticked && (
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: canTick ? 4 : 2.5 }}>
                    {ticked}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Collapse>
    </Paper>
  );
}

export default function SettlingInPage() {
  const { can } = usePermissions();
  const canRead = can('student_onboarding', 'read');
  const canTick = can('student_onboarding', 'write');
  const [status, setStatus] = useState<SettlingInStatus>('open');
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useSettlingIns({ status, page }, canRead);
  const tab = TABS.find((t) => t.value === status)!;
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  // Rows left this page (a tick finished the last one on it): go back to the last page there is,
  // never "nothing here" with no pager (/qa F-L2).
  const pastTheEnd = !!data && data.total > 0 && rows.length === 0 && page > pages;
  useEffect(() => {
    if (pastTheEnd) setPage(pages);
  }, [pastTheEnd, pages]);

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        title="Onboarding check-in"
        subtitle={
          data
            ? `Each new student's checklist, due ${data.windowDays} days after their first class.`
            : "Each new student's checklist for their first weeks."
        }
        breadcrumbs={[{ label: 'Admin', path: '/admin' }, { label: 'Onboarding check-in' }]}
      />

      <Tabs
        value={status}
        onChange={(_e, v: SettlingInStatus) => {
          setStatus(v);
          setPage(1);
        }}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 2 }}
      >
        {TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>

      {!canRead ? (
        // The nav and guard admit any onboarding permission; the list needs read (/qa F-L3).
        <Alert severity="info">You do not have access to view onboarding check-ins.</Alert>
      ) : isLoading || pastTheEnd ? (
        <Stack spacing={1}>
          <Skeleton variant="rectangular" height={90} />
          <Skeleton variant="rectangular" height={90} />
        </Stack>
      ) : isError ? (
        <Alert severity="error">Failed to load the onboarding check-ins. Please try again.</Alert>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary" data-testid="settling-in-empty">
          {tab.empty}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((row) => (
            <CheckInRow key={row.studentId} row={row} canTick={canTick} />
          ))}
          {pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
              <Pagination count={pages} page={page} onChange={(_e, p) => setPage(p)} />
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}
